import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  ArrowLeft, Download, Users, TrendingUp, AlertCircle,
  Headphones, Mic, BookOpen, PenTool, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_COLORS = {
  0: { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-300', accent: '#9ca3af' },
  1: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-300', accent: '#dc2626' },
  2: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-300', accent: '#ea580c' },
  3: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-300', accent: '#ca8a04' },
  4: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-300', accent: '#2563eb' },
  5: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-300', accent: '#16a34a' },
  6: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-300', accent: '#059669' },
};

const DOMAIN_ICONS = {
  listening: Headphones,
  speaking: Mic,
  reading: BookOpen,
  writing: PenTool,
};

const PrescriptiveReport = ({ classId, className, onBack }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedStudents, setExpandedStudents] = useState({});

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const res = await axios.get(`${API}/classes/${classId}/report-card`);
        setReport(res.data);
        // Auto-expand all groups
        const expanded = {};
        (res.data.groups || []).forEach((g, i) => { expanded[i] = true; });
        setExpandedGroups(expanded);
      } catch (e) {
        toast.error('Failed to load prescriptive report');
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [classId]);

  const handleDownloadPdf = async () => {
    try {
      const res = await axios.get(`${API}/classes/${classId}/report-card/pdf`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Prescriptive_Report_${className?.replace(/\s+/g, '_') || 'class'}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error('Failed to download PDF');
    }
  };

  const toggleGroup = (idx) => {
    setExpandedGroups(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const toggleStudent = (sid) => {
    setExpandedStudents(prev => ({ ...prev, [sid]: !prev[sid] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="text-center p-12 text-gray-500">
        <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p>Failed to load report data.</p>
        <Button variant="outline" className="mt-4 btn-sharp" onClick={onBack}>
          <ArrowLeft size={16} className="mr-2" /> Back
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="prescriptive-report">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Button variant="ghost" size="sm" onClick={onBack} className="h-8 px-2" data-testid="report-back-btn">
              <ArrowLeft size={18} />
            </Button>
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="report-title">
              Prescriptive Report Card
            </h2>
          </div>
          <p className="text-sm text-gray-500 ml-11">
            {report.class_name} &bull; Grade {report.grade_level} &bull; {report.total_students} students
          </p>
        </div>
        <Button onClick={handleDownloadPdf} className="btn-sharp-primary h-10" data-testid="report-download-pdf">
          <Download size={16} className="mr-2" /> Download PDF
        </Button>
      </div>

      {/* v6.32.221 — NYC ELD Pilot curriculum context banner. Shows only when
          the teacher's district is mapped to Reach Higher or English 3D. PRD
          §6: MOSAIC is an assessment platform — pointers route teachers to
          materials they already own; we never produce curriculum content. */}
      {report.curriculum_context && (
        <div
          className="border-l-4 border-[#1E3A8A] bg-[#EEF2FF] p-4 mb-6"
          data-testid="curriculum-context-banner"
        >
          <div className="text-xs font-semibold text-[#1E3A8A] uppercase tracking-wider mb-1">
            NYC ELD Pilot Context
          </div>
          <div className="text-sm text-gray-800">
            District {report.curriculum_context.district_number} is piloting{' '}
            <span className="font-bold" data-testid="curriculum-context-name">
              {report.curriculum_context.curriculum_name}
            </span>{' '}
            <span className="text-gray-600">({report.curriculum_context.publisher}).</span>{' '}
            Results below route instruction back to that curriculum.
          </div>
          <div className="text-xs text-gray-500 mt-2 italic">
            {report.curriculum_context.disclaimer}
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-t border-l border-gray-300 mb-8" data-testid="report-summary">
        <div className="swiss-card">
          <Users className="w-7 h-7 text-blue-600 mb-2" />
          <div className="text-2xl font-bold">{report.total_students}</div>
          <div className="text-xs text-gray-500">Total Students</div>
        </div>
        <div className="swiss-card">
          <TrendingUp className="w-7 h-7 text-green-600 mb-2" />
          <div className="text-2xl font-bold">{report.assessed_count}</div>
          <div className="text-xs text-gray-500">Assessed</div>
        </div>
        <div className="swiss-card">
          <AlertCircle className="w-7 h-7 text-orange-600 mb-2" />
          <div className="text-2xl font-bold">{report.total_students - report.assessed_count}</div>
          <div className="text-xs text-gray-500">Not Assessed</div>
        </div>
        <div className="swiss-card">
          <div className="text-2xl font-bold text-[#002FA7]">{report.groups?.length || 0}</div>
          <div className="text-xs text-gray-500 mt-2">Proficiency Groups</div>
        </div>
      </div>

      {/* Proficiency Groups (Least to Greatest) */}
      <div className="mb-4">
        <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Students by WIDA Proficiency Level
        </h3>
        <p className="text-sm text-gray-500 mb-4">Grouped from lowest to highest proficiency with prescriptive recommendations</p>
      </div>

      <div className="space-y-4" data-testid="report-groups">
        {report.groups?.map((group, idx) => {
          const colors = LEVEL_COLORS[group.level] || LEVEL_COLORS[0];
          const isExpanded = expandedGroups[idx];

          return (
            <div key={idx} className={`border-2 ${colors.border}`} data-testid={`report-group-${group.level}`}>
              {/* Group Header */}
              <div
                className={`p-4 ${colors.bg} cursor-pointer flex items-center justify-between`}
                onClick={() => toggleGroup(idx)}
                data-testid={`group-header-${group.level}`}
              >
                <div className="flex items-center gap-4">
                  <div
                    className={`w-14 h-14 flex items-center justify-center font-bold text-xl text-white`}
                    style={{ backgroundColor: colors.accent }}
                  >
                    L{group.level}
                  </div>
                  <div>
                    <div className="font-bold text-lg">
                      {group.level === 0 ? 'Not Assessed' : `Level ${group.level}: ${group.level_name}`}
                    </div>
                    <div className="text-sm text-gray-600">
                      {group.students?.length || 0} student{group.students?.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
                {isExpanded ? <ChevronUp size={20} className="text-gray-500" /> : <ChevronDown size={20} className="text-gray-500" />}
              </div>

              {isExpanded && (
                <div className="divide-y divide-gray-100">
                  {/* Group Recommendations */}
                  {group.group_recommendations?.length > 0 && (
                    <div className="p-4 bg-white" data-testid={`group-recs-${group.level}`}>
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        Group Instructional Recommendations
                      </div>
                      <ul className="space-y-1.5">
                        {group.group_recommendations.map((rec, ri) => (
                          <li key={ri} className="text-sm text-gray-700 flex items-start gap-2">
                            <span className="text-xs mt-1" style={{ color: colors.accent }}>&#9679;</span>
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Students */}
                  {group.students?.map((student) => {
                    const isStudentExpanded = expandedStudents[student.student_id];
                    return (
                      <div key={student.student_id} className="bg-white" data-testid={`report-student-${student.student_id}`}>
                        <div
                          className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                          onClick={() => student.assessed && toggleStudent(student.student_id)}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-9 h-9 bg-gray-100 flex items-center justify-center text-sm font-semibold text-gray-600">
                              {student.name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                            <div>
                              <div className="font-medium text-sm">{student.name}</div>
                              {student.assessed ? (
                                <div className="text-xs text-gray-500">
                                  Overall: {student.overall_score} &bull; Weakest: {student.weakest_domain} ({student.weakest_score}) &bull; Strongest: {student.strongest_domain} ({student.strongest_score})
                                </div>
                              ) : (
                                <div className="text-xs text-gray-400">No assessment data available</div>
                              )}
                            </div>
                          </div>
                          {student.assessed && (
                            <div className="flex items-center gap-3">
                              <div
                                className="px-2 py-1 text-xs font-bold text-white"
                                style={{ backgroundColor: colors.accent }}
                              >
                                {student.overall_score}
                              </div>
                              {isStudentExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                            </div>
                          )}
                        </div>

                        {/* Expanded Student Domain Details */}
                        {isStudentExpanded && student.domains && (
                          <div className="px-4 pb-4" data-testid={`student-domains-${student.student_id}`}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ml-13">
                              {['listening', 'speaking', 'reading', 'writing'].map((domain) => {
                                const d = student.domains[domain];
                                if (!d) return null;
                                const DomainIcon = DOMAIN_ICONS[domain];
                                const levelColor = LEVEL_COLORS[d.level] || LEVEL_COLORS[0];
                                const barPct = Math.min(((d.score - 1) / 5) * 100, 100);

                                return (
                                  <div key={domain} className="border border-gray-200 p-3" data-testid={`domain-${domain}-${student.student_id}`}>
                                    <div className="flex items-center gap-2 mb-2">
                                      <DomainIcon size={14} className="text-gray-500" />
                                      <span className="text-sm font-semibold capitalize">{domain}</span>
                                      <span className={`ml-auto text-xs px-1.5 py-0.5 font-bold ${levelColor.bg} ${levelColor.text}`}>
                                        L{d.level} - {d.level_name}
                                      </span>
                                    </div>
                                    <Progress value={barPct} className="h-1.5 mb-2" />
                                    <div className="text-xs text-gray-600 mb-1">
                                      <span className="font-medium">Score:</span> {d.score}
                                    </div>
                                    {d.can_do && (
                                      <div className="text-xs text-gray-500 mb-1">
                                        <span className="font-medium">Can do:</span> {d.can_do}
                                      </div>
                                    )}
                                    {d.next_steps && (
                                      <div className="text-xs text-green-700 bg-green-50 p-1.5 mt-1">
                                        <span className="font-medium">Next steps:</span> {d.next_steps}
                                      </div>
                                    )}
                                    {/* v6.32.221 — curriculum pointer. Routes
                                        the teacher to their existing pilot
                                        curriculum without producing content. */}
                                    {d.curriculum_pointer && (
                                      <div
                                        className="text-xs text-[#1E3A8A] bg-indigo-50 p-1.5 mt-1 border-l-2 border-[#1E3A8A]"
                                        data-testid={`curriculum-pointer-${domain}-${student.student_id}`}
                                      >
                                        <span className="font-medium">Curriculum pointer:</span> {d.curriculum_pointer}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="mt-6 text-center text-xs text-gray-400" data-testid="report-footer">
        Generated {new Date(report.generated_at).toLocaleString()} &bull; MOSAIC Assessment Platform
      </div>
    </div>
  );
};

export default PrescriptiveReport;
